import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  Badge,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon,
  BookOnline as BookingIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  SupervisorAccount as SupervisorAccountIcon,
  List as ListIcon,
  Archive as ArchiveIcon,
  Favorite as FavoriteIcon
} from '@mui/icons-material';
import AuthService from '../services/auth';

const Layout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch authentication status and user data
  useEffect(() => {
    const fetchAuthData = async () => {
      try {
        const authStatus = await AuthService.isAuthenticated();
        setIsAuthenticated(authStatus);

        if (authStatus) {
          try {
            const user = await AuthService.getCurrentUser();
            if (user) {
              setCurrentUser(user);
              setIsAdmin(user && user.role === 'ADMIN');
              setIsSuperAdmin(user && user.role === 'SUPERADMIN');
            } else {
              // If user is null but we're authenticated, something is wrong
              console.error('User data is null but authenticated');
              setIsAuthenticated(false);
              AuthService.logout(); // Force logout to clear any invalid state
              return;
            }
          } catch (userError) {
            console.error('Error fetching user data:', userError);
            // If we can't get user data, we should log out
            setIsAuthenticated(false);
            AuthService.logout(); // Force logout to clear any invalid state
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching auth data:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthData();
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Show loading indicator while fetching auth data
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const menuItems = [
    {
      text: 'Інвентар',
      icon: <InventoryIcon />,
      path: '/',
      requireAuth: false
    },
    {
      text: 'Мої бронювання',
      icon: <BookingIcon />,
      path: '/bookings',
      requireAuth: true,
      badge: 0 // Could be dynamic in a real app
    },
    {
      text: 'Оцінювання',
      icon: <FavoriteIcon />,
      path: '/voting',
      requireAuth: true
    },
    {
      text: 'Панель адміна',
      icon: <DashboardIcon />,
      path: '/admin',
      requireAuth: true,
      adminOnly: true
    },
    {
      text: 'Управління інвентарем',
      icon: <InventoryIcon />,
      path: '/admin/inventory',
      requireAuth: true,
      adminOnly: true
    },
    {
      text: 'Архів бронювань',
      icon: <ArchiveIcon />,
      path: '/admin/archived',
      requireAuth: true,
      adminOnly: true
    },
    {
      text: 'Управління ролями',
      icon: <SupervisorAccountIcon />,
      path: '/admin/roles',
      requireAuth: true,
      superAdminOnly: true
    },
    {
      text: 'Список дозволених',
      icon: <ListIcon />,
      path: '/admin/whitelist',
      requireAuth: true,
      superAdminOnly: true
    }
  ];

  const drawer = (
    <Box sx={{ width: '100%', overflow: 'hidden', overflowX: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          p: 3, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #da0606 0%, #b00000 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.1), transparent)',
            zIndex: 1
          }
        }}
      >
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                transform: 'rotate(-90deg)',
                transition: 'all 0.3s ease'
              },
              transition: 'all 0.3s ease',
              zIndex: 2
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold', 
            letterSpacing: 1,
            mb: 2,
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          Бронювання Інвентарю
        </Typography>

        {isAuthenticated && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 1,
              cursor: 'pointer',
              p: 1.5,
              pl: 0,
              borderRadius: 2,
              width: '100%',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => {
              if (isMobile) {
                handleDrawerToggle();
              }
              navigate('/profile');
            }}
          >
            <Avatar 
              sx={{ 
                width: 50, 
                height: 50, 
                bgcolor: 'white',
                color: 'primary.main',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                border: '2px solid rgba(255,255,255,0.8)'
              }}
            >
              {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ ml: 1.5, flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {`${currentUser?.firstName} ${currentUser?.lastName}` || 'Користувач'}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.9, mb: 0.5 }}>
                {currentUser?.email}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {isSuperAdmin ? (
                  <Chip 
                    size="small" 
                    label="Суперадмін" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                ) : isAdmin ? (
                  <Chip 
                    size="small" 
                    label="Адміністратор" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                ) : (
                  <a/>
                )}
              </Typography>
            </Box>
            <KeyboardArrowRightIcon sx={{ opacity: 0.7 }} />
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', py: 2, width: '100%' }}>
        <Typography 
          variant="overline" 
          sx={{ 
            px: 2, 
            color: 'text.secondary', 
            fontWeight: 'bold',
            fontSize: '0.75rem',
            letterSpacing: 1,
            display: 'block',
            width: '100%'
          }}
        >
          МЕНЮ
        </Typography>

        <List sx={{ mt: 1, width: '100%', px: 0 }}>
          {menuItems.map((item) => {
            // Skip items that require authentication if user is not authenticated
            if (item.requireAuth && !isAuthenticated) return null;
            // Skip admin-only items if user is not admin or superadmin
            if (item.adminOnly && !isAdmin && !isSuperAdmin) return null;
            // Skip superadmin-only items if user is not superadmin
            if (item.superAdminOnly && !isSuperAdmin) return null;

            const isActive = location.pathname === item.path;

            return (
              <ListItem 
                button 
                key={item.text} 
                component={Link} 
                to={item.path}
                onClick={isMobile ? handleDrawerToggle : undefined}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  py: 1.2,
                  width: '100%',
                  bgcolor: isActive ? 'rgba(218, 6, 6, 0.08)' : 'transparent',
                  color: isActive ? 'primary.main' : 'text.primary',
                  boxShadow: isActive ? '0 2px 8px rgba(218, 6, 6, 0.1)' : 'none',
                  '&:hover': {
                    bgcolor: 'rgba(218, 6, 6, 0.12)',
                    boxShadow: '0 2px 8px rgba(218, 6, 6, 0.15)',
                    transition: 'all 0.2s ease'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isActive ? 'primary.main' : 'text.secondary',
                    minWidth: 40
                  }}
                >
                  {item.badge !== undefined && item.badge > 0 ? (
                    <Badge 
                      badgeContent={item.badge} 
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.7rem',
                          height: 18,
                          minWidth: 18
                        }
                      }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 'bold' : 'medium',
                    fontSize: '0.95rem'
                  }}
                />
                {isActive && <KeyboardArrowRightIcon color="primary" fontSize="small" />}
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)', width: '100%' }}>
        {isAuthenticated ? (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => {
              AuthService.logout();
              navigate('/login');
            }}
            sx={{
              borderRadius: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 'medium'
            }}
          >
            Вийти з системи
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            component={Link}
            to="/login"
            onClick={isMobile ? handleDrawerToggle : undefined}
            sx={{
              borderRadius: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 'medium'
            }}
          >
            Увійти в систему
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="sticky" 
        elevation={1}
        sx={{
          bgcolor: 'white',
          color: 'text.primary',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ 
          px: { xs: 2, sm: 3, md: 4 },
          height: 64,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: isMobile ? 'space-between' : 'center',
            gap: 2, // Add spacing between elements
            width: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ 
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(218, 6, 6, 0.18)',
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              <Box
                component={Link}
                to="/"
                sx={{ 
                  textDecoration: 'none', 
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <img 
                  src="/lnu_su.png"
                  alt="Logo"
                  style={{ 
                    height: 40,
                    marginRight: 12,
                    objectFit: 'contain'
                  }} 
                />
                <Typography
                  variant="h6"
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 'bold',
                    display: 'block',
                    fontSize: { xs: '0.9rem', sm: '1.25rem' }
                  }}
                >
                  Бронювання Інвентарю
                </Typography>
              </Box>
            </Box>

            {/* Desktop horizontal menu */}
            {!isMobile && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  // Remove flexGrow to prevent pushing other elements
                  mx: 2
                }}
              >
                {menuItems.map((item) => {
                  // Skip items that require authentication if user is not authenticated
                  if (item.requireAuth && !isAuthenticated) return null;
                  // Skip admin-only items if user is not admin or superadmin
                  if (item.adminOnly && !isAdmin && !isSuperAdmin) return null;
                  // Skip superadmin-only items if user is not superadmin
                  if (item.superAdminOnly && !isSuperAdmin) return null;

                  const isActive = location.pathname === item.path;

                  return (
                    <Button
                      key={item.text}
                      component={Link}
                      to={item.path}
                      startIcon={item.badge !== undefined && item.badge > 0 ? (
                        <Badge badgeContent={item.badge} color="primary">
                          {item.icon}
                        </Badge>
                      ) : item.icon}
                      sx={{
                        mx: 1,
                        color: isActive ? 'primary.main' : 'text.primary',
                        fontWeight: isActive ? 'bold' : 'normal',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'rgba(218, 6, 6, 0.05)'
                        },
                        ...(isActive && {
                          borderBottom: '2px solid',
                          borderColor: 'primary.main'
                        })
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                })}
              </Box>
            )}

            {isAuthenticated ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>

                <Box
                  onClick={() => navigate('/profile')}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 40, 
                      height: 40,
                      bgcolor: 'primary.main'
                    }}
                  >
                    {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  component={Link} 
                  to="/login"
                  size="small"
                  sx={{ 
                    mr: 1,
                    display: { xs: isMobile ? 'none' : 'inline-flex', sm: 'inline-flex' }
                  }}
                >
                  Увійти
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  component={Link} 
                  to="/register"
                  size="small"
                  startIcon={isMobile ? <PersonIcon /> : null}
                >
                  {isMobile ? '' : 'Реєстрація'}
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer (temporary) */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block' },
          zIndex: (theme) => theme.zIndex.drawer + 100, // Ensure drawer is above everything
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)',
          },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: { xs: '85%', sm: 320 },
            maxWidth: 320,
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            background: '#ffffff',
            overflowX: 'hidden',
          },
        }}
        transitionDuration={300}
        SlideProps={{
          easing: {
            enter: 'cubic-bezier(0.2, 0, 0.2, 1)',
            exit: 'cubic-bezier(0.2, 0, 0.2, 1)'
          }
        }}
      >
        {drawer}
      </Drawer>

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 3, md: 4 },
          bgcolor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          width: '100%'
        }}
      >
        <Fade in={true} timeout={800}>
          <Container maxWidth="lg">
            <Outlet />
          </Container>
        </Fade>
      </Box>
    </Box>
  );
};

export default Layout;
